<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { Bot, BookOpen, ChevronDown, CircleX, ContactRound, FileClock, Files, LayoutDashboard, LoaderCircle, LogOut, Menu, MessageSquareText, Settings2, WalletCards, X } from 'lucide-vue-next'
import { ENTERPRISE_DISABLED_EVENT } from './api'
import { useAppStore } from './store'

const store = useAppStore()
const route = useRoute()
const mobileOpen = ref(false)
const menuToggle = ref<HTMLButtonElement | null>(null)
const sidebarElement = ref<HTMLElement | null>(null)
const retryButton = ref<HTMLButtonElement | null>(null)
const mobileQuery = window.matchMedia('(max-width: 720px)')
const isMobile = ref(mobileQuery.matches)
const updateViewport = () => { isMobile.value = mobileQuery.matches; mobileOpen.value = false }
mobileQuery.addEventListener('change', updateViewport)
const handleEnterpriseDisabled = () => {
  mobileOpen.value = false
  store.markEnterpriseDisabled()
}
onMounted(async () => {
  window.addEventListener(ENTERPRISE_DISABLED_EVENT, handleEnterpriseDisabled)
  if (store.enterpriseBlocked) {
    await nextTick()
    retryButton.value?.focus()
  }
})
onBeforeUnmount(() => {
  mobileQuery.removeEventListener('change', updateViewport)
  window.removeEventListener(ENTERPRISE_DISABLED_EVENT, handleEnterpriseDisabled)
})
watch(() => store.enterpriseBlocked, async blocked => {
  if (!blocked) return
  await nextTick()
  retryButton.value?.focus()
}, { immediate: true, flush: 'post' })
const navigationExpanded = computed(() => isMobile.value ? mobileOpen.value : !store.menuCollapsed)
const nav = [
  { label: '工作概览', path: '/overview', icon: LayoutDashboard, section: '总览' },
  { label: 'AI 客服', path: '/agents', icon: Bot, section: '业务配置' },
  { label: '联系人', path: '/contacts', icon: ContactRound, section: '业务配置' },
  { label: '知识库', path: '/knowledge', icon: BookOpen, section: '内容管理' },
  { label: '话术库', path: '/scripts', icon: MessageSquareText, section: '内容管理' },
  { label: '记忆', path: '/memories', icon: Files, section: '内容管理' },
  { label: '对话记录', path: '/conversations', icon: FileClock, section: '数据记录' },
  { label: '点数与用量', path: '/points', icon: WalletCards, section: '数据记录' },
  { label: '企业设置', path: '/settings', icon: Settings2, section: '系统' }
]
const sections = computed(() => [...new Set(nav.map(item => item.section))])
const title = computed(() => String(route.meta.title || '企业管理'))
const displayName = computed(() => String(store.session?.name || store.session?.display_name || store.session?.username || '企业管理员'))
const enterpriseName = computed(() => store.enterpriseName)

/** 桌面折叠图标栏；手机打开抽屉并将键盘焦点移入导航。 */
async function toggleNav() {
  if (!isMobile.value) { store.toggleMenu(); return }
  if (mobileOpen.value) { closeNav(); return }
  mobileOpen.value = true
  await nextTick()
  sidebarElement.value?.querySelector<HTMLButtonElement>('button')?.focus()
}

/** 收起手机导航后，焦点回到打开导航的按钮。 */
function closeNav() {
  if (!mobileOpen.value) return
  mobileOpen.value = false
  void nextTick(() => menuToggle.value?.focus())
}

/** 手机抽屉开启时将 Tab 焦点保持在可见导航内。 */
function trapNavigationFocus(event: KeyboardEvent) {
  if (!isMobile.value || !mobileOpen.value) return
  const controls = sidebarElement.value?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
  if (!controls?.length) return
  const first = controls[0]!
  const last = controls[controls.length - 1]!
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
</script>

<template>
  <div class="app-frame" @keydown.esc="closeNav">
    <div v-if="!store.sessionReady" class="gate-screen" role="status">
      <img class="gate-logo" src="/zhiying-logo.png" alt="知应" />
      <LoaderCircle class="loading-spinner" :size="20" aria-hidden="true" />
      <p>正在验证管理会话…</p>
    </div>
    <section v-else-if="store.enterpriseBlocked" class="enterprise-disabled-screen" aria-labelledby="enterprise-disabled-title">
      <div class="enterprise-disabled-status" role="alert">
        <CircleX :size="17" :stroke-width="2" aria-hidden="true" />
        <span>本企业已停用</span>
      </div>
      <div class="enterprise-disabled-content">
        <h1 id="enterprise-disabled-title">本企业已停用</h1>
        <button ref="retryButton" class="enterprise-disabled-retry" type="button" :disabled="store.enterpriseCheckLoading" @click="store.retryEnterpriseCheck()">
          <LoaderCircle v-if="store.enterpriseCheckLoading" class="loading-spinner" :size="16" aria-hidden="true" />
          <span>{{ store.enterpriseCheckLoading ? '正在检查' : '重新检查' }}</span>
        </button>
        <p v-if="store.enterpriseCheckError" class="enterprise-disabled-error" role="alert">{{ store.enterpriseCheckError }}</p>
      </div>
    </section>
    <div v-else-if="!store.authenticated" class="gate-screen">
      <img class="gate-logo" src="/zhiying-logo.png" alt="知应" />
      <h1>进入知应企业工作台</h1>
      <p v-if="store.sessionError" role="alert">暂时无法连接认证服务，请重试。</p>
      <p v-else role="status">正在前往东黎云统一登录…</p>
      <el-button v-if="store.sessionError" type="primary" @click="store.checkSession()">重新连接</el-button>
      <el-button v-else type="primary" @click="store.login()">前往登录</el-button>
    </div>
    <template v-else>
      <header class="topbar" :inert="isMobile && mobileOpen">
        <div class="topbar-left">
          <button ref="menuToggle" class="icon-button collapse-toggle" :aria-label="isMobile ? '打开导航' : store.menuCollapsed ? '展开导航' : '收起导航'" :aria-expanded="navigationExpanded" aria-controls="workspace-navigation" @click="toggleNav"><Menu :size="20" /></button>
          <RouterLink to="/overview" class="brand-lockup" aria-label="知应，返回工作概览">
            <img class="brand-mark" src="/zhiying-logo.png" alt="" />
            <strong>知应</strong><span class="brand-subtitle">企业工作台</span>
          </RouterLink>
        </div>
        <div class="topbar-right">
          <span class="tenant-pill">{{ enterpriseName }}</span>
          <el-dropdown trigger="click" @command="(command: string) => command === 'logout' ? store.logout() : $router.push('/settings')">
            <button class="account-button" aria-label="打开企业账户菜单"><span class="user-avatar" aria-hidden="true">{{ displayName.slice(0, 1) }}</span><span class="account-name">{{ displayName }}</span><ChevronDown :size="14" /></button>
            <template #dropdown><el-dropdown-menu><el-dropdown-item command="settings">企业设置</el-dropdown-item><el-dropdown-item command="logout" divided>退出登录</el-dropdown-item></el-dropdown-menu></template>
          </el-dropdown>
        </div>
      </header>
      <button v-if="mobileOpen" class="mobile-scrim" aria-label="关闭导航" @click="closeNav"></button>
      <aside ref="sidebarElement" :inert="isMobile && !mobileOpen" :aria-hidden="isMobile && !mobileOpen" @keydown.tab="trapNavigationFocus" id="workspace-navigation" class="sidebar" :class="{ 'mobile-open': mobileOpen, collapsed: store.menuCollapsed }">
        <div class="mobile-nav-heading"><strong>工作台导航</strong><button class="icon-button" aria-label="关闭导航" @click="closeNav"><X :size="18" /></button></div>
        <RouterLink to="/settings" class="tenant-switch" title="管理企业设置" @click="closeNav"><span class="tenant-avatar">{{ enterpriseName.slice(0, 1) }}</span><span class="tenant-copy"><strong>{{ enterpriseName }}</strong><small>企业管理空间</small></span><ChevronDown :size="14" /></RouterLink>
        <nav class="side-nav" aria-label="工作台导航">
          <section v-for="section in sections" :key="section" class="nav-section">
            <p class="nav-label">{{ section }}</p>
            <RouterLink v-for="item in nav.filter(entry => entry.section === section)" :key="item.path" :to="item.path" class="nav-link" :class="{ active: route.path === item.path }" :aria-current="route.path === item.path ? 'page' : undefined" :title="item.label" @click="closeNav">
              <component :is="item.icon" :size="18" :stroke-width="1.8" /><span>{{ item.label }}</span>
            </RouterLink>
          </section>
        </nav>
        <div class="sidebar-foot"><div class="sidebar-note"><Bot :size="20" /><span><strong>让经验持续积累</strong><small>应答 · 请教 · 学习</small></span></div><button class="logout-button" @click="store.logout()"><LogOut :size="16" /><span>退出登录</span></button></div>
      </aside>
      <main :inert="isMobile && mobileOpen" class="main-shell" :class="{ expanded: store.menuCollapsed }">
        <div class="breadcrumbs" aria-label="当前位置"><span>企业工作台</span><span class="crumb-sep" aria-hidden="true">/</span><strong>{{ title }}</strong></div>
        <section class="workspace"><RouterView /></section>
        <footer class="app-footer"><span>知应 · 自进化智能客服</span><span>企业工作空间</span></footer>
      </main>
    </template>
  </div>
</template>
